import React, { useEffect, useMemo, useState, useRef } from "react";
import { Box, Typography, Card, Skeleton } from "@mui/material";
import { NewsStore } from "../../store/newsStore";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { BASE_URL } from "../../api/api";

const noImage = "/frontend/assets/logos/logo.png";

const LazyImage = ({ src, alt, sx, className, hideOnError }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      if (imgRef.current.naturalWidth === 0) setError(true);
      else setLoaded(true);
    }
  }, [src]);

  if (error && hideOnError) return null;

  const displaySrc = error ? noImage : src;

  return (
    <Box sx={{ position: "relative", flexShrink: 0, ...sx }}>
      {!loaded && !error && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{ position: "absolute", top: 0, left: 0, zIndex: 1, borderRadius: sx?.borderRadius }}
        />
      )}
      <Box
        component="img"
        ref={imgRef}
        className={className}
        src={displaySrc}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: displaySrc === noImage ? "contain" : (sx?.objectFit || "cover"),
          objectPosition: sx?.objectPosition || "center center",
          borderRadius: sx?.borderRadius,
          opacity: loaded || error ? 1 : 0,
          transition: "opacity 0.4s ease-in-out",
          bgcolor: displaySrc === noImage ? "rgba(0,0,0,0.05)" : "transparent",
        }}
      />
    </Box>
  );
};

export default function RightSidebar() {
  const { news, fetchNews } = NewsStore();

  useEffect(() => {
    if ((!news || news.length === 0) && typeof fetchNews === "function") {
      fetchNews(1, 10);
    }
  }, [fetchNews, news]);

  // Filter to get up to 3 latest news per state, including news without state
  const sidebarNews = useMemo(() => {
    if (!Array.isArray(news) || news.length === 0) return [];

    const stateCounts = new Map();
    const result = [];

    news.forEach((item) => {
      const stateName = item.location?.state;
      if (stateName) {
        const count = stateCounts.get(stateName) || 0;
        if (count < 3) {
          stateCounts.set(stateName, count + 1);
          result.push(item);
        }
      } else {
        result.push(item);
      }
    });

    return result.length > 0 ? result.slice(0, 10) : news.slice(0, 10);
  }, [news]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return noImage;
    return imagePath.startsWith("http") ? imagePath : `${BASE_URL}${imagePath}`;
  };

  const truncateHtmlText = (html, maxLength = 80) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.textContent || div.innerText || "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  return (
    <Box component="aside" sx={{ position: "sticky", top: 20 }}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontSize: "20px",
          ml: 1,
        }}
      >
        <Box
          sx={{
            width: "5px",
            height: "20px",
            background: "linear-gradient(135deg, #ff004c, #ff004c)",
            transform: "skewX(-20deg)",
          }}
        />
        Latest News
        <Box
          sx={{
            width: "5px",
            height: "20px",
            background: "linear-gradient(135deg, #ff004c, #ff004c)",
            transform: "skewX(-20deg)",
          }}
        />
      </Typography>

      {sidebarNews.map((item) => (
        <Link to={`/${item.slug}`} key={item.id} style={{ textDecoration: 'none', color: 'inherit' }}>
          <Card
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              p: 1.2,
              borderRadius: "10px",
              background: "background.default",
              color: "text.primary",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)", // हल्का सा shadow
              transition: "0.3s ease",
              cursor: "pointer",
              "&:hover .sidebar-news-title": { color: "#ff184e" },
            }}
          >
            <LazyImage
              src={getImageUrl(item.image)}
              alt={item.title}
              hideOnError={false}
              sx={{
                width: { xs: 110, sm: 130 },
                height: { xs: 80, sm: 90 },
                borderRadius: 1,
                flexShrink: 0,
                objectPosition: "center center",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* Title */}
              <Typography
                fontWeight={600}
                className="sidebar-news-title"
                sx={{
                  fontSize: "15px",
                  lineHeight: "1.3",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2, // 2 line wrap
                  WebkitBoxOrient: "vertical",
                  transition: "color 0.3s ease",
                }}
              >
                {item.title}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "text.secondary",
                  mt: 0.5,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2, // wrap description bottom
                  WebkitBoxOrient: "vertical",
                }}
              >
                {truncateHtmlText(item.description)}
              </Typography>

              {/* Date */}
              <Typography fontSize="12px" color="text.secondary" sx={{ mt: 0.5 }}>
                {dayjs(item.published_at).format("MMM D, YYYY")}
              </Typography>
            </Box>
          </Card>
        </Link>
      ))}
    </Box>
  );
}
      {/* <Card sx={{ p: 2, mt: 3 }}>
        <Box
          sx={{
            height: 150,
            bgcolor: "yellow",
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: 600,
          }}
        >
          <img
            src="/assets/images/ed61dc32-be10-4804-ba95-7628d062de03.jpeg"
            alt="ads"
            style={{ backgroundPosition: "cover" }}
          />
        </Box>
      </Card> */}
